<template>
  <div style="display:flex;min-height:100vh;">
    <Sidebar />

    <main class="main">
      <Topbar />

      <div class="page-content">

        <!-- Year selector + search -->
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;gap:12px;">
          <div style="display:flex;gap:6px;">
            <div
              v-for="year in availableYears"
              :key="year"
              :class="['reports-year-tab', { active: selectedYear === year }]"
              @click="selectedYear = year"
            >{{ year }}</div>
          </div>
          <div class="search-bar" style="max-width:320px;">
            <svg viewBox="0 0 16 16"><circle cx="7" cy="7" r="4"/><path d="M10.5 10.5l3 3"/></svg>
            <input type="text" v-model="searchQuery" @input="fetchReports" placeholder="Search property or owner…"/>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" style="text-align:center;padding:60px;">
          <div class="spinner"></div>
          <p style="margin-top:12px;color:#9896bb;font-size:14px;">Loading reports...</p>
        </div>

        <!-- Months -->
        <template v-else>
          <div v-if="currentYearData && currentYearData.months.length > 0">
            <div v-for="monthData in currentYearData.months" :key="monthData.month" class="month-row">

              <!-- Month header (clickable) -->
              <div :class="['month-row-header', { open: openMonths.includes(monthData.month) }]" @click="toggleMonth(monthData.month)">
                <div>
                  <div class="month-name">{{ monthData.month_label }} {{ selectedYear }}</div>
                  <div class="month-meta">{{ monthData.total_reports }} reports sent</div>
                </div>
                <div class="month-stats">
                  <div class="month-stat"><div class="month-stat-val">{{ formatCurrency(monthData.commission_ex_vat) }}</div><div class="month-stat-label">Commission ex VAT</div></div>
                  <div class="month-stat"><div class="month-stat-val">{{ formatCurrency(monthData.vat) }}</div><div class="month-stat-label">VAT</div></div>
                  <div class="month-stat"><div class="month-stat-val">{{ formatCurrency(monthData.total_settlements) }}</div><div class="month-stat-label">Total settlements</div></div>
                </div>
                <div :class="['month-chevron', { open: openMonths.includes(monthData.month) }]">
                  <svg viewBox="0 0 16 16"><path d="M4 6l4 4 4-4"/></svg>
                </div>
              </div>

              <!-- Month properties (expandable) -->
              <div v-if="openMonths.includes(monthData.month)" class="month-properties open">
                <table style="width:100%;border-collapse:collapse;">
                  <thead><tr>
                    <th class="th-cell" style="width:25%">Property</th>
                    <th class="th-cell" style="width:14%">Commission ex VAT</th>
                    <th class="th-cell" style="width:10%">VAT</th>
                    <th class="th-cell" style="width:18%">Owner Settlement</th>
                    <th class="th-cell" style="width:13%">Sent</th>
                    <th class="th-cell" style="width:20%;text-align:right">Actions</th>
                  </tr></thead>
                  <tbody>
                    <template v-for="report in monthData.reports" :key="report.id">
                      <tr class="rep-data-row">
                        <td class="td-cell"><div class="prop-name">{{ report.property_name }}</div><div class="prop-owner">{{ report.owner_name }}</div></td>
                        <td class="td-cell"><div class="amount">{{ formatCurrency(report.commission_ex_vat) }}</div></td>
                        <td class="td-cell"><div style="font-size:13px;color:#9896bb">{{ formatCurrency(report.vat) }}</div></td>
                        <td class="td-cell">
                          <div class="pay-status" @click="togglePaid(report)">
                            <div class="ps-amount" :style="report.payment_status === 'paid' ? 'color:#00b894' : 'color:#1a1a2e'">{{ formatCurrency(report.settlement_amount) }}</div>
                            <div class="ps-line" :style="report.payment_status === 'paid' ? 'color:#00b894' : 'color:#b7791f'">
                              {{ report.payment_status === 'paid' ? '✓ Paid' : '● Awaiting payment' }}
                            </div>
                            <div class="ps-hint">Click to {{ report.payment_status === 'paid' ? 'mark unpaid' : 'mark paid' }}</div>
                          </div>
                        </td>
                        <td class="td-cell" style="font-size:13px;color:#9896bb">{{ formatSentDate(report.sent_at) }}</td>
                        <td class="td-cell" style="text-align:right">
                          <div class="action-cell">
                            <button class="btn btn-ghost btn-sm" @click="downloadReport(report)">Download</button>
                            <button class="btn btn-outline btn-sm" @click="toggleEmailPanel(report.id)">Email</button>
                          </div>
                        </td>
                      </tr>
                      <!-- Email panel -->
                      <tr v-if="openEmailPanel === report.id">
                        <td colspan="6" style="padding:0">
                          <div class="rep-email-panel open">
                            <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.07em;color:#9896bb;margin-bottom:10px;">Send report — {{ report.property_name }}</div>
                            <div class="owner-toggle-row">
                              <label>Owner email</label>
                              <div class="toggle-wrap">
                                <label class="toggle-switch">
                                  <input type="checkbox" v-model="emailIncludeOwner"/>
                                  <div class="toggle-track"></div>
                                </label>
                                <span class="owner-email-val">{{ report.owner_email }}</span>
                              </div>
                            </div>
                            <div class="email-row"><label>Also send to</label><input class="email-input" type="email" v-model="emailAlsoSendTo" placeholder="e.g. bookkeeper, accountant…"/></div>
                            <div class="email-actions">
                              <button class="btn btn-ghost btn-sm" @click="openEmailPanel = null">Cancel</button>
                              <button class="btn btn-primary btn-sm" @click="sendReportEmail(report)">Send report</button>
                            </div>
                            <div class="email-note">Owner email is set in Property Settings.</div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>

            </div>
          </div>

          <!-- Empty state -->
          <div v-else class="month-row">
            <div class="month-row-header" style="cursor:default;">
              <div><div class="month-name" style="color:#9896bb">No reports found for {{ selectedYear }}</div></div>
            </div>
          </div>
        </template>

      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';
import Topbar from '@/components/Topbar.vue';

export default {
  name: 'ReportsArchiveView',
  components: { Sidebar, Topbar },
  data() {
    return {
      loading: true,
      archiveData: [],
      selectedYear: new Date().getFullYear(),
      searchQuery: '',
      openMonths: [],
      openEmailPanel: null,
      emailIncludeOwner: false,
      emailAlsoSendTo: ''
    };
  },
  computed: {
    baseUrl() {
      if (window.location.pathname.startsWith('/dev')) return '/dev';
      return process.env.VUE_APP_API_URL || '';
    },
    availableYears() {
      if (this.archiveData.length === 0) return [new Date().getFullYear()];
      return this.archiveData.map(y => y.year);
    },
    currentYearData() {
      return this.archiveData.find(y => y.year === this.selectedYear) || null;
    }
  },
  methods: {
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
    },
    formatSentDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      const day = d.getDate();
      const month = d.toLocaleString('en-GB', { month: 'short' });
      const hours = d.getHours().toString().padStart(2, '0');
      const mins = d.getMinutes().toString().padStart(2, '0');
      return `${day} ${month} · ${hours}:${mins}`;
    },
    toggleMonth(month) {
      const idx = this.openMonths.indexOf(month);
      if (idx >= 0) this.openMonths.splice(idx, 1);
      else this.openMonths.push(month);
    },
    toggleEmailPanel(reportId) {
      if (this.openEmailPanel === reportId) {
        this.openEmailPanel = null;
      } else {
        this.openEmailPanel = reportId;
        this.emailIncludeOwner = false;
        this.emailAlsoSendTo = '';
      }
    },
    async togglePaid(report) {
      const previous = report.payment_status;
      report.payment_status = previous === 'paid' ? 'unpaid' : 'paid';
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${this.baseUrl}/api/pms/monthly-reports/${report.id}/toggle-payment-status/`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.data && response.data.success) {
          report.payment_status = response.data.payment_status;
          report.paid_at = response.data.paid_at;
        } else {
          report.payment_status = previous;
        }
      } catch (error) {
        console.error('Error toggling payment status:', error);
        report.payment_status = previous;
        alert('Could not update payment status. Please try again.');
      }
    },
    async fetchReports() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig') || 'null');
        if (!selectedPms || !selectedPms.id) {
          this.archiveData = [];
          this.loading = false;
          return;
        }
        const params = { pms_config_id: selectedPms.id };
        if (this.searchQuery) params.search = this.searchQuery;

        const response = await axios.get(`${this.baseUrl}/api/pms/reports-archive/`, {
          params,
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success) {
          this.archiveData = response.data.data.years;
          this.archiveData.forEach(year => {
            year.months.forEach(month => {
              month.reports.forEach(report => {
                if (!report.payment_status) report.payment_status = 'unpaid';
              });
            });
          });
        }
      } catch (error) {
        console.error('Error fetching reports archive:', error);
      } finally {
        this.loading = false;
      }
    },
    async downloadReport(report) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${this.baseUrl}/api/pms/monthly-reports/${report.id}/download/`, {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `Report_${report.property_name.replace(/ /g, '_')}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading report:', error);
        alert('Error downloading report');
      }
    },
    async sendReportEmail(report) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${this.baseUrl}/api/pms/monthly-reports/${report.id}/send/`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        alert(`Report sent to ${report.owner_email}`);
        this.openEmailPanel = null;
      } catch (error) {
        console.error('Error sending report:', error);
        alert(error.response?.data?.message || 'Error sending report');
      }
    }
  },
  mounted() {
    this.fetchReports();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600&display=swap');

* { box-sizing: border-box; margin: 0; padding: 0; }

.main { margin-left: 240px; flex: 1; display: flex; flex-direction: column; min-height: 100vh; background: #f5f4fb; font-family: 'Inter', sans-serif; }
.page-content { padding: 24px 28px; }

/* Year tabs */
.reports-year-tab { padding: 7px 18px; border-radius: 20px; font-size: 14px; font-weight: 600; cursor: pointer; border: 1.5px solid #e8e6f4; background: #ffffff; color: #4a4880; transition: all 0.15s; }
.reports-year-tab.active { background: #5b4fcf; color: #fff; border-color: #5b4fcf; }

/* Search bar */
.search-bar { flex: 1; display: flex; align-items: center; gap: 10px; background: #ffffff; border: 1.5px solid #e8e6f4; border-radius: 8px; padding: 8px 14px; }
.search-bar svg { width: 14px; height: 14px; fill: none; stroke: #9896bb; stroke-width: 2; flex-shrink: 0; }
.search-bar input { border: none; outline: none; font-size: 14px; color: #1a1a2e; background: transparent; width: 100%; font-family: 'Inter', sans-serif; }
.search-bar input::placeholder { color: #9896bb; }

/* Month rows */
.month-row { background: #ffffff; border: 1px solid #e8e6f4; border-radius: 10px; margin-bottom: 8px; overflow: hidden; box-shadow: 0 1px 4px rgba(26,24,61,0.07); }
.month-row-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; cursor: pointer; user-select: none; transition: background 0.15s; }
.month-row-header:hover { background: #faf9ff; }
.month-row-header.open { border-bottom: 1px solid #e8e6f4; }
.month-name { font-family: 'Outfit', sans-serif; font-size: 1rem; font-weight: 700; color: #1a1a2e; }
.month-meta { font-size: 13px; color: #9896bb; margin-top: 2px; }
.month-stats { display: flex; gap: 20px; align-items: center; }
.month-stat { text-align: right; }
.month-stat-val { font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 700; color: #1a1a2e; }
.month-stat-label { font-size: 11px; color: #9896bb; text-transform: uppercase; letter-spacing: 0.05em; }
.month-chevron { width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; color: #9896bb; transition: transform 0.2s; flex-shrink: 0; margin-left: 16px; }
.month-chevron.open { transform: rotate(180deg); }
.month-chevron svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2; }

.month-properties { display: block; }

/* Table */
.th-cell { padding: 12px 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #9896bb; border-bottom: 1px solid #e8e6f4; text-align: left; }
.td-cell { padding: 13px 20px; vertical-align: middle; font-size: 14px; border-bottom: 1px solid #f0eef8; background: #ffffff; }
.rep-data-row:last-child .td-cell { border-bottom: none; }
.rep-data-row:hover .td-cell { background: #faf9ff; }
.prop-name { font-weight: 600; color: #1a1a2e; font-size: 14px; }
.prop-owner { font-size: 13px; color: #9896bb; margin-top: 1px; }
.amount { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 14px; }
.action-cell { display: flex; align-items: center; gap: 7px; justify-content: flex-end; }

/* Pay status */
.pay-status { cursor: pointer; display: inline-flex; flex-direction: column; gap: 2px; padding: 4px 7px; border-radius: 7px; border: 1.5px solid transparent; transition: background 0.15s, border-color 0.15s; user-select: none; }
.pay-status:hover { background: #f0eefb; border-color: #e8e6f4; }
.ps-amount { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 14px; }
.ps-line { font-size: 12px; display: flex; align-items: center; gap: 4px; }
.ps-hint { font-size: 11px; color: #9896bb; opacity: 0; transition: opacity 0.15s; margin-top: 1px; }
.pay-status:hover .ps-hint { opacity: 1; }

/* Email panel */
.rep-email-panel { background: #f9f8ff; border-top: 1px solid #e8e6f4; padding: 14px 20px 16px; }
.rep-email-panel.open { display: block; }
.owner-toggle-row { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.owner-toggle-row label { font-size: 13px; color: #9896bb; width: 90px; flex-shrink: 0; }
.toggle-wrap { display: flex; align-items: center; gap: 8px; flex: 1; }
.toggle-switch { position: relative; width: 34px; height: 18px; flex-shrink: 0; }
.toggle-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
.toggle-track { position: absolute; inset: 0; background: #e8e6f4; border-radius: 99px; transition: background 0.2s; cursor: pointer; }
.toggle-switch input:checked + .toggle-track { background: #5b4fcf; }
.toggle-track::after { content: ''; position: absolute; width: 12px; height: 12px; background: #fff; border-radius: 50%; top: 3px; left: 3px; transition: transform 0.2s; }
.toggle-switch input:checked + .toggle-track::after { transform: translateX(16px); }
.owner-email-val { font-size: 13px; color: #9896bb; flex: 1; }
.email-row { display: flex; gap: 8px; align-items: center; margin-bottom: 8px; }
.email-row label { font-size: 13px; color: #9896bb; width: 90px; flex-shrink: 0; }
.email-input { flex: 1; border: 1.5px solid #e8e6f4; border-radius: 7px; padding: 8px 12px; font-size: 14px; color: #1a1a2e; font-family: 'Inter', sans-serif; background: #ffffff; outline: none; }
.email-input:focus { border-color: #5b4fcf; }
.email-actions { display: flex; gap: 8px; margin-top: 10px; justify-content: flex-end; }
.email-note { font-size: 12px; color: #9896bb; margin-top: 6px; }

/* Buttons */
.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 7px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; font-family: 'Inter', sans-serif; transition: opacity 0.15s; white-space: nowrap; }
.btn:hover { opacity: 0.88; }
.btn-primary { background: #5b4fcf; color: #fff; }
.btn-outline { background: transparent; color: #4a4880; border: 1.5px solid #e8e6f4; }
.btn-ghost { background: #f5f4fb; color: #4a4880; }
.btn-sm { padding: 6px 13px; font-size: 13px; }

/* Spinner */
.spinner { width: 32px; height: 32px; border: 3px solid #e8e6f4; border-top-color: #5b4fcf; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
